// Overview (zoom level 1 → 2, DESIGN §3): the stage stepper S01–S22 with each stage's engine state and the reason for every
// wait; the selected stage's steps with owners and what is missing; every step by status; my tasks here; risks; team and
// attestations; the engine and its lease; time; and "changed since you last looked" (audit seq vs the seq this viewer last saw,
// kept per viewer in the browser — a convenience, never audit state).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { AgentChip, Button, Card, DataTable, StatusPill, Stepper, Timeline, countsText, type Column } from "../../components";
import type { Activity, DocModel, StageStep } from "../../data/types";
import { addressedTo } from "../../lib/commands";
import { ago, fmtDate, fmtStamp, plural, titleCase } from "../../lib/format";
import { useLoad, usePref } from "../../lib/hooks";
import { useEngagement } from "./Layout";
import { AskOverview, BeforeReleaseItems, DeadlineLine, DeadlineList, DeadlineRecorder, RiskSummaryView, hasOverview } from "./overviewParts";
import { Act, Facts, QueueList, asList, asStrs, useAct, useDocs } from "./shared";

const SETTLED = new Set(["done", "not_applicable", "not_tracked"]);

export function OverviewPage() {
  const { model, id } = useEngagement();
  const { me, ds } = useApp();
  const { open, drawer } = useAct();
  const sStages = model.stages.filter((s) => s.stageId.startsWith("S"));
  const fStages = model.stages.filter((s) => !s.stageId.startsWith("S"));
  // Open on the first S-stage the engine does not report settled.
  const current = sStages.find((s) => !SETTLED.has(s.state)) ?? sStages[0] ?? model.stages[0];
  const [sel, setSel] = useState<string | undefined>(current?.stageId);
  const stage = model.stages.find((s) => s.stageId === sel);
  const mine = model.actions.filter((a) => addressedTo(a.who, me.personName, me.role) === "me");
  const anyone = model.actions.filter((a) => a.human && a.who && addressedTo(a.who, me.personName, me.role) === "anyone");
  const allSteps = sStages.flatMap((s) => s.steps);
  const planning = useDocs("planning");
  const time = useDocs("time_summary");
  const beat = useLoad(() => ds.heartbeat(), [ds]);
  const activity = useLoad(() => ds.table(id, "activity"), [ds, id]);
  const attest = useLoad(() => ds.table(id, "attestations"), [ds, id]);
  const h = model.header;

  const cols: Array<Column<StageStep>> = [
    { id: "id", header: "Step", cell: (s) => <span className="font-mono text-xs">{s.stepId}</span>, sortValue: (s) => s.stepId },
    { id: "name", header: "What", cell: (s) => s.name },
    { id: "state", header: "State", cell: (s) => <StatusPill state={s.state} />, sortValue: (s) => s.state },
    { id: "owner", header: "Owner", cell: (s) => <span className="text-xs">{s.owner.join(", ") || "—"}</span> },
    { id: "why", header: "Why / what is missing", cell: (s) => (
      <span className="block text-xs text-muted">{s.reason ?? ""}
        {s.gaps.map((g, i) => (
          <span key={i} className="mt-1 flex flex-wrap items-center gap-1">
            <span>{g.text}{g.human ? " (a person's act)" : ""}</span>
            {g.command && <Act command={g.command} title={g.text} open={open} label={`${g.command}…`} />}
          </span>
        ))}
      </span>) },
  ];

  return (
    <div className="space-y-4">
      <Card title="Stages" bodyClass="p-3">
        {sStages.length ? <Stepper stages={sStages} selected={sel} onSelect={setSel} /> : <p className="text-sm text-muted">The engine has not published lifecycle stages for this engagement yet.</p>}
        {fStages.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted">
            <span>Firm stages:</span>
            {fStages.map((f) => (
              <button key={f.stageId} type="button" onClick={() => setSel(f.stageId)} aria-pressed={sel === f.stageId} title={f.reason ?? f.state}
                className={`rounded border px-1.5 py-0.5 ${sel === f.stageId ? "border-brand text-brand" : "border-line hover:text-ink"}`}>
                {f.stageId} · {titleCase(f.state)}
              </button>
            ))}
          </div>
        )}
        {stage && (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold">{stage.stageId} {stage.name}</h3><StatusPill state={stage.state} />
              <span className="text-xs text-muted">{countsText(stage.counts)}</span>
            </div>
            {stage.reason && <p className="text-sm text-muted">{stage.state === "done" ? "" : "Why: "}{stage.reason}</p>}
            <DataTable rows={stage.steps} columns={cols} rowKey={(s) => s.stepId} empty="No steps." dense caption={`Steps of ${stage.stageId}`}
              filter={{ id: "state", label: "state", value: (s) => s.state, labelFor: titleCase }} />
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title={`My tasks here · ${mine.length}`} className="min-w-0 lg:col-span-2" bodyClass="p-3">
          <QueueList actions={mine} open={open} empty="Nothing addressed to you by name on this engagement." />
          {anyone.length > 0 && <p className="mt-2 text-xs text-muted">{plural(anyone.length, "more act")} for anyone on the team — see Flow.</p>}
        </Card>
        <Changed id={id} activity={activity.data} error={activity.error} />
      </div>

      <Card title="All steps by status" bodyClass="p-3">
        <DataTable rows={allSteps} rowKey={(s) => s.stepId} dense caption="All steps by status" empty="No steps published."
          filter={{ id: "state", label: "state", value: (s) => s.state, labelFor: titleCase }}
          columns={[
            { id: "stage", header: "Stage", cell: (s) => <span className="font-mono text-xs">{s.stageId}</span>, sortValue: (s) => s.stageId },
            ...cols.slice(0, 4),
          ]} />
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Deadlines" className="min-w-0" action={h.overviewAsOf ? <span className="text-xs text-muted">as of {fmtDate(h.overviewAsOf)} (engine)</span> : undefined}>
          {hasOverview(h) ? (
            <div className="space-y-3">
              {h.nextDeadline && <p className="text-sm">Next: <DeadlineLine d={h.nextDeadline} /></p>}
              <DeadlineList deadlines={h.deadlines} />
              <DeadlineRecorder engagementId={id} current={h.deadlines.find((d) => d.what === "report_due")} />
            </div>
          ) : <div className="space-y-3"><AskOverview /><DeadlineRecorder engagementId={id} current={undefined} /></div>}
        </Card>
        <Card title={`Before release · ${h.counts.beforeRelease}`} className="min-w-0" action={<Link to={`/e/${id}/details/release`} className="text-xs text-brand hover:underline">Release</Link>}>
          <BeforeReleaseItems h={h} />
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card title="Risks" className="min-w-0">
          {h.overviewError ? <p role="alert" className="text-sm text-blocked">The engine could not compute the overview: {h.overviewError}</p>
            : h.risk ? <RiskSummaryView risk={h.risk} /> : hasOverview(h) ? <p className="text-xs text-muted">No risk assessment recorded yet.</p> : null}
          {!h.risk && <SignificantRisks docs={planning.data} />}
        </Card>
        <Card title="Team" className="min-w-0">
          <ul className="space-y-1 text-sm">{model.team.map((t) => <li key={t.person + t.role} className="flex justify-between gap-2"><span>{t.person}</span><span className="text-muted">{titleCase(t.role)}</span></li>)}</ul>
          <div className="mt-3 flex flex-wrap gap-1"><Act command="team.add" title="Add a team member" open={open} label="Add member…" /><Act command="eqr.appoint" title="Appoint the EQR" open={open} label="Appoint EQR…" /></div>
          <h3 className="mt-3 text-xs font-medium uppercase tracking-wide text-muted">Attestations</h3>
          {(attest.data ?? []).length === 0 ? <p className="text-xs text-muted">None recorded.</p> : (
            <ul className="mt-1 space-y-1 text-xs">{attest.data!.filter((a) => !a.supersededBy).map((a) => <li key={a.id}><span className="font-medium">{titleCase(a.subject)}</span>: {a.statement} <span className="text-muted">· {a.by} · {fmtStamp(a.at)}</span></li>)}</ul>
          )}
          <div className="mt-2"><Act command="attest.record" title="Record an attestation" open={open} label="Attest…" /></div>
        </Card>
        <Card title="Engine and lease" className="min-w-0">
          <Facts items={[
            ["Lease", h.lease ? `${h.lease.holderName} (${h.lease.engineId})${h.lease.expiresAt ? ` until ${fmtStamp(h.lease.expiresAt)}` : ""}` : "No engine holds the lease"],
            ["Record head", <span key="h" className="font-mono text-xs" title={h.auditHead ?? undefined}>{h.auditHead ? `${h.auditHead.slice(0, 12)}…` : "—"}{h.auditSeq !== null ? ` · seq ${h.auditSeq}` : ""}</span>],
            ["Published", `${fmtStamp(h.syncedAt)} (${ago(h.syncedAt)})`],
            ["Heartbeat", beat.data?.length ? beat.data.map((b) => `${b.holderName} · ${ago(b.lastSeen)} · ${b.inboxPending} pending`).join("; ") : "none published"],
          ]} />
          <p className="mt-2 text-xs text-muted">Taking, releasing or handing off the lease runs on the engine's machine:</p>
          <p className="font-mono text-[11px] text-muted">yvc lease take · yvc lease handoff</p>
          <div className="mt-2"><Act command="checks.run" title="Run the engagement checks" open={open} label="Run checks…" /></div>
        </Card>
        <Card title="Time and release" className="min-w-0" action={<Link to={`/e/${id}/details/release`} className="text-xs text-brand hover:underline">Release</Link>}>
          <TimeFacts docs={time.data} />
          <div className="mt-2 flex flex-wrap gap-1"><Act command="time.budget" title="Record the time budget" open={open} label="Budget…" /></div>
          <div className="mt-3 border-t border-line pt-2 text-sm">
            {model.failed.release ? <StatusPill state="failed" label="Release readiness not computed" /> : model.release ? (model.release.ready ? <StatusPill state="ready" label="Ready for release" /> : <StatusPill state="blocked" label={plural(model.release.blockers.length, "blocker")} />) : <span className="text-muted">Release readiness not published.</span>}
            <p className="mt-1 text-xs text-muted">{plural(h.counts.openItems, "open item")} · {h.counts.beforeRelease} before release · {h.counts.rendered}/{h.counts.workpapers} workpapers rendered · {h.counts.signedOff} signed off (engine counts)</p>
          </div>
        </Card>
      </div>
      {drawer}
    </div>
  );
}

function SignificantRisks({ docs }: { docs: DocModel[] | undefined }) {
  const d = docs?.find((x) => x.kind === "risks");
  if (!d) return null;
  if (d.error) return <p className="mt-2 text-xs text-blocked">Risks could not be computed: {d.error}</p>;
  const doc = (d.doc ?? {}) as Record<string, unknown>;
  const sig = asStrs(doc.significant).length ? asStrs(doc.significant)
    : asList(doc.risks).filter((r) => r.significant === 1 || r.significant === true || r.fraud === 1 || r.fraud === true).map((r) => `${String(r.area)} ${String(r.assertion)} (RMM ${String(r.rmm)})`);
  return (
    <div className="mt-2">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted">Significant risks (risk.list)</h3>
      {sig.length ? <ul className="mt-1 list-disc pl-4 text-sm">{sig.map((s) => <li key={s}>{s}</li>)}</ul> : <p className="text-xs text-muted">None recorded as significant.</p>}
    </div>
  );
}

function TimeFacts({ docs }: { docs: DocModel[] | undefined }) {
  const d = docs?.find((x) => x.kind === "time");
  if (!d) return <p className="text-xs text-muted">Time not published.</p>;
  if (d.error) return <p className="text-xs text-blocked">Time could not be computed: {d.error}</p>;
  const t = (d.doc ?? {}) as Record<string, unknown>;
  return <Facts items={[["Hours", String(t.hours ?? "—")], ["Unbilled", `${String(t.unbilled_hours ?? "—")} h · ${String(t.unbilled_amount ?? "—")}`],
    ["By phase", asList(t.by_phase).map((p) => `${String(p.phase)} ${String(p.hours)}`).join(" · ") || "—"]]} />;
}

/** What changed since you last looked: audit log entries after the seq this viewer marked as seen (stored in this browser). */
function Changed({ id, activity, error }: { id: string; activity: Activity[] | undefined; error?: string }) {
  const [seen, setSeen] = usePref<number | null>(`seen:${id}`, null);
  const latest = useMemo(() => (activity ?? []).reduce((m, a) => Math.max(m, a.seq), 0), [activity]);
  const fresh = (activity ?? []).filter((a) => seen === null || a.seq > seen).sort((a, b) => b.seq - a.seq);
  return (
    <Card title={seen === null ? "Recent changes" : `Changed since you last looked · ${fresh.length}`} className="min-w-0"
      action={latest > 0 && latest !== seen ? <Button size="sm" onClick={() => setSeen(latest)}>Mark all as seen</Button> : undefined}>
      {error ? <p className="text-sm text-blocked">{error}</p> : !activity ? <p className="text-sm text-muted">Loading…</p> : (
        <>
          <p className="mb-2 text-xs text-muted">{seen === null ? "First visit on this device: the latest entries of the audit log." : `Audit log entries after seq ${seen}.`}</p>
          {fresh.length === 0 ? <p className="text-sm text-muted">Nothing new since seq {seen}.</p> : (
            <Timeline label="Changes since you last looked" events={fresh.slice(0, 8).map((a) => ({
              id: String(a.seq), at: a.at, tone: a.agent ? "agent" as const : "person" as const,
              title: <span className="inline-flex flex-wrap items-center gap-1"><span className="font-mono text-xs">#{a.seq} {a.command}</span> · {a.actor}{a.agent && <AgentChip name={a.agent} />}</span>,
              detail: a.redacted ? "inputs withheld (blind reading or sampled act)" : a.summary || undefined,
            }))} />
          )}
          {fresh.length > 8 && <p className="mt-1 text-xs text-muted">{fresh.length - 8} more in Health → Activity.</p>}
        </>
      )}
    </Card>
  );
}
